import React, { useEffect, useRef, useState } from "react"
import "leaflet/dist/leaflet.css"
import useT from "../../hooks/useT"
import { ShippingOffices } from "../../storeApi"
import { officeAddress } from "./providers"

// Sofia: where the map opens when nothing says better (no office picked, no location, no town found)
const SOFIA = { lat: 42.6977, lng: 23.3219 }
const PIN = (on) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40"><path d="M15 0C6.7 0 0 6.6 0 14.8 0 25.9 15 40 15 40s15-14.1 15-25.2C30 6.6 23.3 0 15 0z" fill="${on ? "#1f2937" : "#e2533a"}"/><circle cx="15" cy="14.5" r="5.5" fill="#fff"/></svg>`

const hasPoint = (office) => Number.isFinite(Number(office?.lat)) && Number.isFinite(Number(office?.lng)) && office.lat !== null && office.lng !== null

/**
 * The pickup points on a map (OpenStreetMap, Leaflet) for a courier with no
 * map of its own to embed (providers.js): the nearest ones around the map's
 * centre, asked again as it is moved. A pin opens the office, Choose picks it.
 * Opens on the office picked before, else where the buyer is (near me), else
 * the town's offices the list found.
 */
export default function OfficeMap({ option, selected, point, offices: listed = [], onPick, picking }) {
  const t = useT()
  const box = useRef(null)
  const state = useRef({ map: null, L: null, markers: new Map(), run: 0 })
  const [failed, setFailed] = useState(false)
  // the texts the popups are built with, current without rebuilding the map
  const texts = useRef({})
  texts.current = { choose: t`Choose`, loading: t`Loading the office…` }
  const pickRef = useRef(onPick)
  pickRef.current = onPick

  useEffect(() => {
    let alive = true
    const s = state.current
    ;(async () => {
      const L = (await import("leaflet")).default
      if (!alive || !box.current) return
      s.L = L
      const start = [selected, ...listed].find(hasPoint)
      const center = hasPoint(selected) ? selected : point || start || SOFIA
      const map = L.map(box.current, { zoomControl: true, attributionControl: true }).setView([Number(center.lat), Number(center.lng)], hasPoint(selected) || point ? 15 : start ? 13 : 12)
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "&copy; OpenStreetMap" }).addTo(map)
      s.map = map
      if (point) L.circleMarker([point.lat, point.lng], { radius: 7, color: "#fff", weight: 2, fillColor: "#2563eb", fillOpacity: 1 }).addTo(map)
      const place = (list) => {
        for (const office of list) {
          if (!hasPoint(office) || s.markers.has(office.id)) continue
          const on = selected?.id === office.id
          const marker = L.marker([Number(office.lat), Number(office.lng)], { icon: L.divIcon({ html: PIN(on), className: "", iconSize: [30, 40], iconAnchor: [15, 40], popupAnchor: [0, -36] }), title: office.name, zIndexOffset: on ? 1000 : 0 })
          marker.bindPopup(() => {
            const el = document.createElement("div")
            el.style.minWidth = "200px"
            const name = document.createElement("div")
            name.style.cssText = "font-weight:700;font-size:15px;margin-bottom:2px"
            name.textContent = office.name
            el.appendChild(name)
            for (const line of [officeAddress(office), office.hours].filter(Boolean)) {
              const p = document.createElement("div")
              p.style.cssText = "font-size:13px;color:#6b7280"
              p.textContent = line
              el.appendChild(p)
            }
            const button = document.createElement("button")
            button.type = "button"
            button.textContent = texts.current.choose
            button.style.cssText = "margin-top:8px;width:100%;height:36px;border-radius:9999px;background:#e2533a;color:#fff;font-weight:700;border:0;cursor:pointer"
            button.setAttribute("data-testid", "office-map-choose")
            button.onclick = () => {
              button.textContent = texts.current.loading
              button.disabled = true
              pickRef.current(office.id)
            }
            el.appendChild(button)
            return el
          })
          marker.addTo(map)
          s.markers.set(office.id, marker)
        }
      }
      place([selected, ...listed].filter(Boolean))
      // the office picked before: its popup open, so it is seen at once
      if (selected?.id) s.markers.get(selected.id)?.openPopup()
      // the nearest pickup points around the centre, asked once the map stops moving
      const load = async () => {
        const id = ++s.run
        const c = map.getCenter()
        const res = await ShippingOffices({ providerId: option.providerId, method: option.method, lat: c.lat, lng: c.lng, limit: 100, sellerId: option.sellerId || undefined }).catch(() => null)
        if (!alive || id !== s.run) return
        if (!res?.offices) return setFailed(true)
        setFailed(false)
        place(res.offices)
      }
      map.on("moveend", load)
      load()
    })().catch(() => alive && setFailed(true))
    return () => {
      alive = false
      s.map?.remove()
      s.map = null
      s.markers = new Map()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option?.key])

  return (
    <div className="relative flex-1 min-h-[360px] flex flex-col">
      <div ref={box} className="flex-1 min-h-0 w-full md:border md:border-bordered md:rounded-lg overflow-hidden z-0" data-testid="office-map" />
      {failed && <p className="absolute left-3 right-3 bottom-3 z-[500] m-0 rounded-md bg-paper px-3 py-2 text-[14px] text-error shadow">{t`The offices could not be loaded`}</p>}
      {picking && <p className="absolute left-3 right-3 top-3 z-[500] m-0 rounded-md bg-paper px-3 py-2 text-[14px] text-text_secondary shadow">{t`Loading the office…`}</p>}
    </div>
  )
}
